import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { RefreshCw } from "lucide-react";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import MobileTabBar from "./components/layout/MobileTabBar";
import FloatingCTA from "./components/layout/FloatingCTA";
import Sidebar from "./components/layout/Sidebar";
import OfflineBanner from "./components/layout/OfflineBanner";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import LoginModal from "./components/auth/LoginModal";
import ProfileSetupModal from "./components/auth/ProfileSetupModal";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageSpinner } from "./components/ui/Spinner";
import { useAuthStore } from "./stores/authStore";
import { useAuth } from "./hooks/useAuth";
import { useThemeStore } from "./stores/themeStore";
import { ROLES } from "./config/constants";
import { partnerApi } from "./services/partnerApi";
import { useSWUpdate } from "./pwa/useSWUpdate";
import { useInstallPrompt } from "./pwa/useInstallPrompt";
import { usePendingQueue } from "./pwa/usePendingQueue";
import { useSessionRestore } from "./hooks/useSessionRestore";

// --- Lazy-loaded pages ---
// Customer
const HomePage = lazy(() => import("./pages/customer/HomePage"));
const ServicesPage = lazy(() => import("./pages/customer/ServicesPage"));
const ServiceDetailPage = lazy(
  () => import("./pages/customer/ServiceDetailPage"),
);
const BookingsPage = lazy(() => import("./pages/customer/BookingsPage"));
const BookingDetailPage = lazy(
  () => import("./pages/customer/BookingDetailPage"),
);
const BookingWizardPage = lazy(
  () => import("./pages/customer/BookingWizardPage"),
);
const WalletPage = lazy(() => import("./pages/customer/WalletPage"));
const ProfilePage = lazy(() => import("./pages/customer/ProfilePage"));
const CarsPage = lazy(() => import("./pages/customer/CarsPage"));
const AddCarPage = lazy(() => import("./pages/customer/AddCarPage"));
const CarDetailPage = lazy(() => import("./pages/customer/CarDetailPage"));
const ServiceHistoryPage = lazy(
  () => import("./pages/customer/ServiceHistoryPage"),
);
const SubscriptionsPage = lazy(
  () => import("./pages/customer/SubscriptionsPage"),
);
const MySubscriptionPage = lazy(
  () => import("./pages/customer/MySubscriptionPage"),
);
const ComplaintsPage = lazy(() => import("./pages/customer/ComplaintsPage"));
const ComplaintDetailPage = lazy(
  () => import("./pages/customer/ComplaintDetailPage"),
);
const ReferralPage = lazy(() => import("./pages/customer/ReferralPage"));
const MilestonePage = lazy(() => import("./pages/customer/MilestonePage"));
const PaymentStatusPage = lazy(
  () => import("./pages/customer/PaymentStatusPage"),
);
const PaymentSuccessPage = lazy(
  () => import("./pages/customer/PaymentSuccessPage"),
);

// Technician
const TechDashboardPage = lazy(
  () => import("./pages/technician/TechDashboardPage"),
);
const TechJobsPage = lazy(() => import("./pages/technician/TechJobsPage"));
const TechBookingDetailPage = lazy(
  () => import("./pages/technician/TechBookingDetailPage"),
);
const TechProfilePage = lazy(
  () => import("./pages/technician/TechProfilePage"),
);
const TechAttendancePage = lazy(
  () => import("./pages/technician/TechAttendancePage"),
);

// Channel Partner
const PartnerDashboardPage = lazy(
  () => import("./pages/partner/PartnerDashboardPage"),
);
const PartnerJobsPage = lazy(() => import("./pages/partner/PartnerJobsPage"));
const PartnerBookingDetailPage = lazy(
  () => import("./pages/partner/PartnerBookingDetailPage"),
);
const PartnerProfilePage = lazy(
  () => import("./pages/partner/PartnerProfilePage"),
);
const PartnerSettlementsPage = lazy(
  () => import("./pages/partner/PartnerSettlementsPage"),
);

// Admin
const AdminDashboardPage = lazy(
  () => import("./pages/admin/AdminDashboardPage"),
);
const ManageBookingsPage = lazy(
  () => import("./pages/admin/ManageBookingsPage"),
);
const ManageUsersPage = lazy(() => import("./pages/admin/ManageUsersPage"));
const ManageRolesPage = lazy(() => import("./pages/admin/ManageRolesPage"));
const NoAccessPage = lazy(() => import("./pages/admin/NoAccessPage"));
const ManageTechniciansPage = lazy(
  () => import("./pages/admin/ManageTechniciansPage"),
);
const ManageChannelPartnersPage = lazy(
  () => import("./pages/admin/ManageChannelPartnersPage"),
);
const PartnerDetailPage = lazy(
  () => import("./pages/admin/PartnerDetailPage"),
);
const PartnerSettlementsAdminPage = lazy(
  () => import("./pages/admin/PartnerSettlementsPage"),
);
const ManageCategoriesPage = lazy(
  () => import("./pages/admin/ManageCategoriesPage"),
);
const ManagePackagesPage = lazy(
  () => import("./pages/admin/ManagePackagesPage"),
);
const ManagePlansPage = lazy(() => import("./pages/admin/ManagePlansPage"));
const ManageSubscriptionsPage = lazy(
  () => import("./pages/admin/ManageSubscriptionsPage"),
);
const ManageComplaintsPage = lazy(
  () => import("./pages/admin/ManageComplaintsPage"),
);
const RevenueReportPage = lazy(() => import("./pages/admin/RevenueReportPage"));
const ManagePricingPage = lazy(() => import("./pages/admin/ManagePricingPage"));
const ManageInventoryPage = lazy(
  () => import("./pages/admin/ManageInventoryPage"),
);
const ManageInventoryCategoriesPage = lazy(
  () => import("./pages/admin/ManageInventoryCategoriesPage"),
);
const ManageRefundsPage = lazy(() => import("./pages/admin/ManageRefundsPage"));
const WhatsAppInboxPage = lazy(() => import("./pages/admin/WhatsAppInboxPage"));
const ManageWarrantyClaimsPage = lazy(
  () => import("./pages/admin/ManageWarrantyClaimsPage"),
);
const ManageRRPage = lazy(() => import("./pages/admin/ManageRRPage"));
const OperationsReportPage = lazy(
  () => import("./pages/admin/OperationsReportPage"),
);
const AuditLogPage = lazy(() => import("./pages/admin/AuditLogPage"));
const ManageCarMasterPage = lazy(
  () => import("./pages/admin/ManageCarMasterPage"),
);
const ManageLocationMasterPage = lazy(
  () => import("./pages/admin/ManageLocationMasterPage"),
);
const AssistedBookingPage = lazy(
  () => import("./pages/admin/AssistedBookingPage"),
);
const ManageLeadsPage = lazy(() => import("./pages/admin/ManageLeadsPage"));
const LeadDetailPage = lazy(() => import("./pages/admin/LeadDetailPage"));
const BookingAccountsPage = lazy(() => import("./pages/admin/BookingAccountsPage"));
const AssistedCompletionPage = lazy(
  () => import("./pages/admin/AssistedCompletionPage"),
);
const ManageCouponsPage = lazy(
  () => import("./pages/admin/ManageCouponsPage"),
);
const ManagePaymentsPage = lazy(() => import("./pages/admin/ManagePaymentsPage"));
const ManageBlogPage = lazy(() => import("./pages/admin/ManageBlogPage"));
const BlogEditorPage = lazy(() => import("./pages/admin/BlogEditorPage"));
const ManageFaqPage = lazy(() => import("./pages/admin/ManageFaqPage"));

// Customer (additional)
const MyRRRequestsPage = lazy(
  () => import("./pages/customer/MyRRRequestsPage"),
);

// Blog + FAQ (customer)
const BlogListPage = lazy(() => import("./pages/customer/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/customer/BlogPostPage"));
const BlogCategoryPage = lazy(() => import("./pages/customer/BlogCategoryPage"));
const FaqPage = lazy(() => import("./pages/customer/FaqPage"));

// Shared
const WarrantyLookupPage = lazy(
  () => import("./pages/shared/WarrantyLookupPage"),
);
const NotFoundPage = lazy(() => import("./pages/shared/NotFoundPage"));
const ErrorPage = lazy(() => import("./pages/shared/ErrorPage"));
const AboutPage = lazy(() => import("./pages/shared/AboutPage"));
const ContactPage = lazy(() => import("./pages/shared/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/shared/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/shared/TermsPage"));
const RefundPage = lazy(() => import("./pages/shared/RefundPage"));

function CustomerLayout({ children, onLoginClick }) {
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <OfflineBanner />
      <Navbar onLoginClick={onLoginClick} />
      <ErrorBoundary>
        <Suspense fallback={<PageSpinner />}>{children}</Suspense>
      </ErrorBoundary>
      <Footer />
      <FloatingCTA />
      <MobileTabBar />
    </div>
  );
}

function PanelLayout({ children, variant }) {
  return (
    <div className="min-h-screen bg-neutral-50 md:flex">
      <Sidebar variant={variant} />
      <div className="flex-1 flex flex-col min-h-screen">
        <OfflineBanner />
        <ErrorBoundary>
          <Suspense fallback={<PageSpinner />}>{children}</Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}

// Human-readable labels for dead-letter reason codes emitted by the SW.
// Defined at module scope so the object is created once, not on every render.
const DL_REASON_LABEL = {
  auth_401: "Session expired",
  auth_403: "Permission denied",
  max_retries: "Too many retries",
  queue_overflow: "Queue was full",
  unknown: "Unknown error",
};

export default function App() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [profileSetupOpen, setProfileSetupOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, role } = useAuthStore();
  useSessionRestore();
  const { theme } = useThemeStore();
  const queryClient = useQueryClient();

  // PWA: update detection + install prompt
  const { updateReady, applyUpdate } = useSWUpdate();
  const { installable, promptInstall } = useInstallPrompt();
  const {
    pendingCount,
    isSyncing,
    drainNow,
    deadLetterCount,
    deadLetterItems,
    dismissDeadLetter,
    dismissAllDead,
    retryDeadLetter,
    retryingIds,
  } = usePendingQueue();

  // Show a persistent error toast when actions have permanently failed.
  // Each item shows its URL path + reason code so it's actionable.
  // Dismissed with a 300 ms delay so rapid dismiss → re-add cycles don't
  // cause a visible flicker (cleanup cancels the timer if count rebounds).
  useEffect(() => {
    if (deadLetterCount === 0) {
      const timer = setTimeout(() => toast.dismiss("sw-dead-letter"), 300);
      return () => clearTimeout(timer);
    }
    toast(
      (t) => (
        <div className="flex flex-col gap-2 min-w-0 max-w-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-red-700">
              {deadLetterCount} action{deadLetterCount > 1 ? "s" : ""} failed
              permanently
            </span>
            <button
              onClick={async () => {
                await dismissAllDead();
                toast.dismiss(t.id);
              }}
              className="text-xs text-neutral-400 hover:text-neutral-600 shrink-0 underline"
            >
              Clear all
            </button>
          </div>
          <ul className="flex flex-col gap-1">
            {deadLetterItems.slice(0, 3).map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2 text-xs bg-red-50 rounded px-2 py-1"
              >
                <span className="truncate text-neutral-700 flex-1">
                  <span className="font-mono text-red-600 mr-1">
                    {item.method}
                  </span>
                  {new URL(item.url).pathname.replace("/api/v1", "")}
                  <span className="ml-1 text-neutral-400">
                    — {DL_REASON_LABEL[item.reason] ?? DL_REASON_LABEL.unknown}
                  </span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => retryDeadLetter(item.id)}
                    disabled={retryingIds.has(item.id)}
                    className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Retry"
                    title="Move back to queue and retry"
                  >
                    {retryingIds.has(item.id) ? "…" : "↺"}
                  </button>
                  <button
                    onClick={() => dismissDeadLetter(item.id)}
                    className="text-neutral-400 hover:text-red-600"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
            {deadLetterItems.length > 3 && (
              <li className="text-xs text-neutral-400 text-center">
                +{deadLetterItems.length - 3} more
              </li>
            )}
          </ul>
        </div>
      ),
      { duration: Infinity, id: "sw-dead-letter" },
    );
  }, [
    deadLetterCount,
    deadLetterItems,
    dismissDeadLetter,
    dismissAllDead,
    retryDeadLetter,
    retryingIds,
  ]);

  // Show a persistent "pending actions" banner when items are queued offline.
  // Uses the same 300 ms delayed-dismiss pattern to prevent flicker.
  useEffect(() => {
    if (pendingCount === 0) {
      const timer = setTimeout(() => toast.dismiss("sw-pending"), 300);
      return () => clearTimeout(timer);
    }
    toast(
      (t) => (
        <div className="flex items-center gap-3">
          <span className="text-sm">
            {pendingCount} action{pendingCount > 1 ? "s" : ""} pending sync
          </span>
          <button
            onClick={() => {
              drainNow();
            }}
            disabled={isSyncing}
            className="px-3 py-1 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors shrink-0"
          >
            {isSyncing ? "Retrying…" : "Retry Now"}
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-neutral-400 hover:text-neutral-600 text-lg leading-none shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ),
      { duration: Infinity, id: "sw-pending" },
    );
  }, [pendingCount, isSyncing, drainNow]);

  // Show update toast when a new SW version is waiting to activate.
  //
  // Admin / technician panels stay open for hours — add a 60-second auto-refresh
  // so stale sessions self-heal without requiring manual action. The toast acts
  // as a visible countdown warning before the silent reload fires.
  // Customer pages keep the toast indefinitely (a booking wizard mid-flight
  // should not auto-reload).
  useEffect(() => {
    if (!updateReady) return;

    const isPanel =
      role === ROLES.ADMIN ||
      role === ROLES.TECHNICIAN ||
      role === ROLES.CHANNEL_PARTNER;
    const AUTO_REFRESH_MS = 60_000;

    toast(
      (t) => (
        <div className="flex flex-col gap-2.5 min-w-[220px]">
          <div className="flex items-start gap-2.5">
            <RefreshCw size={15} className="text-primary-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-neutral-900 leading-tight">
                Update ready
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {isPanel
                  ? "New version available — page will refresh in ~60 s"
                  : "New features & fixes are ready"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                applyUpdate();
              }}
              className="flex-1 px-3 py-1.5 text-xs font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              Refresh Now
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1.5 text-xs text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-100 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, id: "sw-update" },
    );

    // Admin / technician: auto-refresh when the countdown expires.
    // Customers are never auto-refreshed — they may be mid-booking.
    if (!isPanel) return;
    const timer = setTimeout(() => {
      toast.dismiss("sw-update");
      applyUpdate();
    }, AUTO_REFRESH_MS);
    return () => clearTimeout(timer);
  }, [updateReady, applyUpdate, role]);

  // Show a one-time install prompt toast (bottom-of-screen, dismissible)
  const [installToastShown, setInstallToastShown] = useState(false);
  useEffect(() => {
    if (!installable || installToastShown) return;
    setInstallToastShown(true);
    toast(
      (t) => (
        <div className="flex items-center gap-3">
          <img
            src="/dm_logo-updated.png"
            alt=""
            className="w-7 h-7 rounded-lg"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              Install MechOnGo Automobiles
            </p>
            <p className="text-xs text-neutral-500">Fast, offline-ready app</p>
          </div>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              await promptInstall();
            }}
            className="px-3 py-1 text-xs font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shrink-0"
          >
            Install
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-neutral-400 hover:text-neutral-600 text-lg leading-none shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ),
      { duration: 12000, id: "sw-install" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installable]);

  // Listen for background sync completion and notify the user.
  // Query invalidation is debounced (500 ms) so that if an SSE update and a
  // SW_SYNC_COMPLETE arrive within the same tick (e.g. after replay of a
  // booking that also triggers a server push), TanStack Query only refetches
  // once instead of twice.
  const syncInvalidateTimer = useRef(null);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      if (event.data?.type !== "SW_SYNC_COMPLETE") return;
      const { succeeded, failed, deadLettered } = event.data;

      if (succeeded > 0) {
        toast.success(
          `${succeeded} queued request${succeeded > 1 ? "s" : ""} sent successfully.`,
        );
        // Debounced invalidation: collapse duplicate triggers within 500 ms.
        clearTimeout(syncInvalidateTimer.current);
        syncInvalidateTimer.current = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["bookings"] });
          queryClient.invalidateQueries({ queryKey: ["complaints"] });
        }, 500);
      }
      if (failed > 0) {
        toast.error(
          `${failed} request${failed > 1 ? "s" : ""} couldn't be sent. Will retry.`,
        );
      }
      if (deadLettered > 0) {
        toast.error(
          `${deadLettered} action${deadLettered > 1 ? "s" : ""} failed permanently after too many retries.`,
          { duration: 8000 },
        );
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
      clearTimeout(syncInvalidateTimer.current);
    };
  }, [queryClient]);

  // Apply / remove the `dark` class on <html> whenever the stored theme changes.
  useEffect(() => {
    const root = document.documentElement;
    const isDark =
      theme === "dark" ||
      (theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    root.classList.toggle("dark", isDark);

    // Also react to OS-level changes when theme === "system"
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => root.classList.toggle("dark", e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  // Auto-init: try to restore session
  const { meQuery } = useAuth();

  // Show login modal if redirected with showLogin state.
  // Immediately replace history entry to clear the flag — prevents mobile
  // bfcache / back-navigation from re-opening the modal on the next visit.
  useEffect(() => {
    if (location.state?.showLogin) {
      setLoginOpen(true);
      navigate(location.pathname + location.search, {
        replace: true,
        state: { from: location.state.from },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.showLogin]);

  // Also open login modal via a `?login=true` query param — lets external
  // sites (e.g. the customer-seo-web Navbar) link directly to the login flow
  // without first bouncing through a protected route. Strip the param from
  // the URL immediately after, same reasoning as the showLogin flag above.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("login") === "true") {
      setLoginOpen(true);
      params.delete("login");
      const newSearch = params.toString();
      navigate(location.pathname + (newSearch ? `?${newSearch}` : ""), {
        replace: true,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const isTechRoute = location.pathname.startsWith("/technician");

  return (
    <>
      <Routes>
        {/* -------- Admin panel -------- */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <PanelLayout variant="admin">
                <Routes>
                  <Route index element={<ProtectedRoute moduleKey="dashboard"><AdminDashboardPage /></ProtectedRoute>} />
                  <Route path="bookings" element={<ProtectedRoute moduleKey="bookings"><ManageBookingsPage /></ProtectedRoute>} />
                  <Route path="users" element={<ProtectedRoute moduleKey="users"><ManageUsersPage /></ProtectedRoute>} />
                  <Route
                    path="technicians"
                    element={<ProtectedRoute moduleKey="technicians"><ManageTechniciansPage /></ProtectedRoute>}
                  />
                  <Route
                    path="channel-partners"
                    element={<ProtectedRoute moduleKey="channel_partners"><ManageChannelPartnersPage /></ProtectedRoute>}
                  />
                  <Route
                    path="channel-partners/:id"
                    element={<ProtectedRoute moduleKey="channel_partners"><PartnerDetailPage /></ProtectedRoute>}
                  />
                  <Route
                    path="settlements"
                    element={<ProtectedRoute moduleKey="settlements"><PartnerSettlementsAdminPage /></ProtectedRoute>}
                  />
                  <Route path="categories" element={<ProtectedRoute moduleKey="categories"><ManageCategoriesPage /></ProtectedRoute>} />
                  <Route path="packages" element={<ProtectedRoute moduleKey="packages"><ManagePackagesPage /></ProtectedRoute>} />
                  <Route path="plans" element={<ProtectedRoute moduleKey="plans"><ManagePlansPage /></ProtectedRoute>} />
                  <Route
                    path="subscriptions"
                    element={<ProtectedRoute moduleKey="subscriptions"><ManageSubscriptionsPage /></ProtectedRoute>}
                  />
                  <Route path="complaints" element={<ProtectedRoute moduleKey="complaints"><ManageComplaintsPage /></ProtectedRoute>} />
                  <Route
                    path="complaints/:id"
                    element={<ProtectedRoute moduleKey="complaints"><ManageComplaintsPage /></ProtectedRoute>}
                  />
                  <Route path="revenue" element={<ProtectedRoute moduleKey="reports"><RevenueReportPage /></ProtectedRoute>} />
                  <Route
                    path="reports/revenue"
                    element={<ProtectedRoute moduleKey="reports"><RevenueReportPage /></ProtectedRoute>}
                  />
                  <Route path="pricing" element={<ProtectedRoute moduleKey="car_master"><ManagePricingPage /></ProtectedRoute>} />
                  <Route path="inventory" element={<ProtectedRoute moduleKey="inventory"><ManageInventoryPage /></ProtectedRoute>} />
                  <Route
                    path="inventory-categories"
                    element={<ProtectedRoute moduleKey="inventory"><ManageInventoryCategoriesPage /></ProtectedRoute>}
                  />
                  <Route path="refunds" element={<ProtectedRoute moduleKey="refunds"><ManageRefundsPage /></ProtectedRoute>} />
                  <Route path="payments" element={<ProtectedRoute moduleKey="payments"><ManagePaymentsPage /></ProtectedRoute>} />
                  <Route path="whatsapp" element={<ProtectedRoute moduleKey="whatsapp"><WhatsAppInboxPage /></ProtectedRoute>} />
                  <Route
                    path="warranty-claims"
                    element={<ProtectedRoute moduleKey="warranty_claims"><ManageWarrantyClaimsPage /></ProtectedRoute>}
                  />
                  <Route path="warranty" element={<ProtectedRoute moduleKey="warranty_claims"><WarrantyLookupPage /></ProtectedRoute>} />
                  <Route path="rr" element={<ProtectedRoute moduleKey="rr"><ManageRRPage /></ProtectedRoute>} />
                  <Route
                    path="reports/operations"
                    element={<ProtectedRoute moduleKey="reports_operations"><OperationsReportPage /></ProtectedRoute>}
                  />
                  <Route path="audit" element={<ProtectedRoute moduleKey="audit"><AuditLogPage /></ProtectedRoute>} />
                  <Route path="car-master" element={<ProtectedRoute moduleKey="car_master"><ManageCarMasterPage /></ProtectedRoute>} />
                  <Route path="location-master" element={<ProtectedRoute moduleKey="location_master"><ManageLocationMasterPage /></ProtectedRoute>} />
                  <Route path="assisted-booking" element={<ProtectedRoute moduleKey="bookings"><AssistedBookingPage /></ProtectedRoute>} />
                  <Route path="bookings/:id/assisted-completion" element={<ProtectedRoute moduleKey="bookings"><AssistedCompletionPage /></ProtectedRoute>} />
                  <Route path="leads" element={<ProtectedRoute moduleKey="leads"><ManageLeadsPage /></ProtectedRoute>} />
                  <Route path="leads/:id" element={<ProtectedRoute moduleKey="leads"><LeadDetailPage /></ProtectedRoute>} />
                  <Route path="booking-accounts" element={<ProtectedRoute moduleKey="booking_accounts"><BookingAccountsPage /></ProtectedRoute>} />
                  <Route path="coupons" element={<ProtectedRoute moduleKey="coupons"><ManageCouponsPage /></ProtectedRoute>} />
                  <Route path="blog" element={<ProtectedRoute moduleKey="blog"><ManageBlogPage /></ProtectedRoute>} />
                  <Route path="blog/new" element={<ProtectedRoute moduleKey="blog"><BlogEditorPage /></ProtectedRoute>} />
                  <Route path="blog/:id/edit" element={<ProtectedRoute moduleKey="blog"><BlogEditorPage /></ProtectedRoute>} />
                  <Route path="faq" element={<ProtectedRoute moduleKey="faq"><ManageFaqPage /></ProtectedRoute>} />
                  <Route path="roles" element={<ProtectedRoute moduleKey="roles"><ManageRolesPage /></ProtectedRoute>} />
                  <Route path="no-access" element={<NoAccessPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </PanelLayout>
            </ProtectedRoute>
          }
        />

        {/* -------- Technician panel -------- */}
        <Route
          path="/technician/*"
          element={
            <ProtectedRoute roles={[ROLES.TECHNICIAN, ROLES.ADMIN]}>
              <PanelLayout variant="technician">
                <Routes>
                  <Route index element={<TechDashboardPage />} />
                  <Route path="jobs" element={<TechJobsPage />} />
                  <Route path="jobs/:id" element={<TechBookingDetailPage />} />
                  <Route path="profile" element={<TechProfilePage />} />
                  <Route path="attendance" element={<TechAttendancePage />} />
                  <Route path="warranty" element={<WarrantyLookupPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </PanelLayout>
            </ProtectedRoute>
          }
        />

        {/* -------- Channel Partner panel -------- */}
        <Route
          path="/partner/*"
          element={
            <ProtectedRoute roles={[ROLES.CHANNEL_PARTNER, ROLES.ADMIN]}>
              <PanelLayout variant="partner">
                <Routes>
                  <Route index element={<PartnerDashboardPage />} />
                  <Route path="jobs" element={<PartnerJobsPage />} />
                  <Route
                    path="jobs/:id"
                    element={<PartnerBookingDetailPage />}
                  />
                  <Route path="profile" element={<PartnerProfilePage />} />
                  <Route
                    path="settlements"
                    element={<PartnerSettlementsPage />}
                  />
                  <Route
                    path="warranty"
                    element={
                      <ProtectedRoute moduleKey="WARRANTY_LOOKUP">
                        <WarrantyLookupPage lookupFn={partnerApi.getWarrantyLookup} />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </PanelLayout>
            </ProtectedRoute>
          }
        />

        {/* -------- Customer routes -------- */}
        <Route
          path="/*"
          element={
            <CustomerLayout onLoginClick={() => setLoginOpen(true)}>
              <Routes>
                <Route
                  path="/"
                  element={
                    isAuthenticated && role === ROLES.ADMIN ? (
                      <Navigate to="/admin" replace />
                    ) : isAuthenticated && role === ROLES.TECHNICIAN ? (
                      <Navigate to="/technician" replace />
                    ) : isAuthenticated && role === ROLES.CHANNEL_PARTNER ? (
                      <Navigate to="/partner" replace />
                    ) : (
                      <HomePage />
                    )
                  }
                />
                <Route path="/services" element={<ServicesPage />} />
                <Route
                  path="/services/:categoryId"
                  element={<ServiceDetailPage />}
                />
                <Route path="/subscriptions" element={<SubscriptionsPage />} />
                <Route path="/payment-status" element={<PaymentStatusPage />} />
                <Route
                  path="/payment-success"
                  element={<PaymentSuccessPage />}
                />

                {/* Blog & FAQ */}
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/faq" element={<FaqPage />} />

                {/* Static / informational pages */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/refund" element={<RefundPage />} />

                {/* Auth-required customer routes */}
                <Route
                  path="/book"
                  element={
                    <ProtectedRoute>
                      <BookingWizardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/book-service"
                  element={
                    <ProtectedRoute>
                      <BookingWizardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bookings"
                  element={
                    <ProtectedRoute>
                      <BookingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bookings/:id"
                  element={
                    <ProtectedRoute>
                      <BookingDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute>
                      <WalletPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cars"
                  element={
                    <ProtectedRoute>
                      <CarsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cars/add"
                  element={
                    <ProtectedRoute>
                      <AddCarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cars/:id"
                  element={
                    <ProtectedRoute>
                      <CarDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cars/:carId/history"
                  element={
                    <ProtectedRoute>
                      <ServiceHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/complaints"
                  element={
                    <ProtectedRoute>
                      <ComplaintsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/complaints/:id"
                  element={
                    <ProtectedRoute>
                      <ComplaintDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rr"
                  element={
                    <ProtectedRoute>
                      <MyRRRequestsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/referral"
                  element={
                    <ProtectedRoute>
                      <ReferralPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/milestones"
                  element={
                    <ProtectedRoute>
                      <MilestonePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscriptions/my"
                  element={
                    <ProtectedRoute>
                      <MySubscriptionPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </CustomerLayout>
          }
        />
      </Routes>

      <LoginModal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        redirectTo={location.state?.from?.pathname || null}
        onNewUser={() => setProfileSetupOpen(true)}
      />
      <ProfileSetupModal
        isOpen={profileSetupOpen}
        onClose={() => setProfileSetupOpen(false)}
      />
    </>
  );
}
